(() => {
  'use strict';
  const TTL = 2 * 60 * 60 * 1000;
  const LIMIT = 25;
  const MARKERS = ['lic_return', 'lic_resume'];
  const STATE_KEY = 'licCollectionReturnV1';
  const RECORD_KEY = 'lic.collection-return.206983135518.v1';
  const ENTRY_KEY = `${RECORD_KEY}.entries`;

  function safeUrl(value, origin) {
    try {
      const url = new URL(value, origin);
      return url.origin === origin && !url.username && !url.password ? url : null;
    } catch { return null; }
  }
  function productHandle(value, origin) {
    const url = safeUrl(value, origin);
    return url?.pathname.match(/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?(?:collections\/[^/]+\/)?products\/([^/]+)\/?$/i)?.[1] || null;
  }
  function collectionUrl(value, origin) {
    const url = safeUrl(value, origin);
    return url && /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?collections\/[^/]+(?:\/(?!products(?:\/|$))[^/]+)?\/?$/i.test(url.pathname) ? url : null;
  }
  function sameView(a, b, origin) {
    const left = collectionUrl(a, origin), right = collectionUrl(b, origin);
    if (!left || !right || left.pathname !== right.pathname || left.hash !== right.hash) return false;
    const values = url => [...url.searchParams].filter(([key]) => !MARKERS.includes(key)).map(pair => JSON.stringify(pair)).sort();
    return JSON.stringify(values(left)) === JSON.stringify(values(right));
  }
  function fresh(record, now = Date.now()) {
    return !!record && typeof record.token === 'string' && typeof record.url === 'string' &&
      typeof record.title === 'string' && typeof record.target === 'string' &&
      Number.isFinite(record.createdAt) && record.createdAt <= now && now - record.createdAt <= TTL &&
      Number.isFinite(record.scrollY) && record.scrollY >= 0;
  }
  function addMarker(value, key, token, origin) {
    const url = safeUrl(value, origin);
    if (!url || !MARKERS.includes(key) || !/^[a-zA-Z0-9_-]{8,80}$/.test(token)) return null;
    if (url.searchParams.has(key)) url.searchParams.delete(key);
    return `${url.origin}${url.pathname}${url.search}${url.search ? '&' : '?'}${key}=${token}${url.hash}`;
  }
  function resolveContext({ href, origin, handle, historyState, entryToken, readRecord, now = Date.now() }) {
    const url = safeUrl(href, origin);
    if (!url || productHandle(url.href, origin) !== handle) return null;
    for (const token of [url.searchParams.get('lic_return'), historyState?.[STATE_KEY], entryToken]) {
      if (typeof token !== 'string') continue;
      let record;
      try { record = readRecord(token); } catch { continue; }
      if (fresh(record, now) && record.token === token && record.target === handle && collectionUrl(record.url, origin)) return record;
    }
    return null;
  }
  function prune(records, now = Date.now()) {
    return Object.fromEntries(Object.entries(records || {}).filter(([token, record]) => record?.token === token && fresh(record, now))
      .sort((a, b) => b[1].createdAt - a[1].createdAt).slice(0, LIMIT));
  }
  if (typeof module === 'object' && module.exports) {
    module.exports = { productHandle, collectionUrl, sameView, fresh, addMarker, resolveContext, prune, TTL, LIMIT };
    return;
  }

  const loader = document.currentScript;
  if (loader?.dataset.licTheme !== '206983135518') return;
  const origin = location.origin;
  const entryHref = loader.dataset.licEntryUrl || location.href;
  const page = loader.dataset.licPage;
  if (!['collection', 'product'].includes(page)) return;

  function readObject(storage, key) {
    try {
      const value = JSON.parse(storage.getItem(key) || '{}');
      return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
    } catch { return {}; }
  }
  function storageObject(name, key) {
    try { return readObject(window[name], key); } catch { return {}; }
  }
  function records() {
    return prune({ ...storageObject('localStorage', RECORD_KEY), ...storageObject('sessionStorage', RECORD_KEY) });
  }
  function writeObject(name, key, value) {
    try { window[name].setItem(key, JSON.stringify(value)); return true; } catch { return false; }
  }
  function saveRecord(record) {
    const saved = prune({ ...records(), [record.token]: record });
    const local = writeObject('localStorage', RECORD_KEY, saved);
    const session = writeObject('sessionStorage', RECORD_KEY, saved);
    return local || session;
  }
  function entryKey() { return window.navigation?.currentEntry?.key || null; }
  function rememberEntry(token) {
    const key = entryKey();
    if (!key) return;
    const entries = storageObject('sessionStorage', ENTRY_KEY);
    entries[key] = token;
    const retained = new Set(Object.keys(records()));
    writeObject('sessionStorage', ENTRY_KEY, Object.fromEntries(Object.entries(entries).filter(([, value]) => retained.has(value)).slice(-LIMIT)));
  }
  function mergeHistory(token, url) {
    const previous = history.state && typeof history.state === 'object' ? history.state : {};
    history.replaceState({ ...previous, [STATE_KEY]: token }, '', url);
  }
  function headerBottom() {
    const header = document.querySelector('#header-component .row-group');
    return header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
  }
  function cardFor(anchor) {
    return anchor.closest('.product-grid__item, .product-card-wrapper, .card-wrapper') || anchor;
  }
  function productAnchor(target) {
    const anchor = target instanceof Element ? target.closest('a[href]') : null;
    return anchor?.closest('main') && productHandle(anchor.href, origin) ? anchor : null;
  }
  function newToken() {
    return window.crypto?.randomUUID?.() || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  }
  function newCollectionRecord(anchor) {
    const url = collectionUrl(location.href, origin);
    if (!url) return null;
    MARKERS.forEach(key => { if (url.searchParams.has(key)) url.searchParams.delete(key); });
    const title = ['all', 'cloud-search-all-products'].includes(loader.dataset.licCollectionHandle)
      ? 'Shop All' : loader.dataset.licCollectionTitle || document.querySelector('main h1')?.textContent.trim() || 'collection';
    return {
      token: newToken(), createdAt: Date.now(), url: url.href, title,
      target: productHandle(anchor.href, origin), scrollY: Math.max(0, scrollY), width: innerWidth,
      anchor: { handle: productHandle(anchor.href, origin), offset: cardFor(anchor).getBoundingClientRect().top - headerBottom() }
    };
  }
  function init() {
    let context = null;
    if (page === 'product') {
      const handle = loader.dataset.licProductHandle;
      const key = entryKey();
      context = resolveContext({ href: entryHref, origin, handle, historyState: history.state,
        entryToken: key && storageObject('sessionStorage', ENTRY_KEY)[key], readRecord: token => records()[token] });
      const clean = safeUrl(location.href, origin);
      const hadMarker = clean.searchParams.has('lic_return');
      clean.searchParams.delete('lic_return');
      if (context) {
        mergeHistory(context.token, clean.href);
        rememberEntry(context.token);
      } else if (hadMarker) {
        history.replaceState(history.state, '', clean.href);
      }
      const updateReturn = () => {
        if (!context) return;
        const link = document.querySelector('.lic-product-return a');
        if (!link) return;
        link.href = addMarker(context.url, 'lic_resume', context.token, origin);
        link.textContent = `Back to ${context.title}`;
      };
      updateReturn();
      const refreshReturn = event => {
        const link = event.target instanceof Element ? event.target.closest('.lic-product-return a') : null;
        if (!link || !context) return;
        context = { ...context, createdAt: Date.now() };
        saveRecord(context);
        updateReturn();
      };
      for (const type of ['pointerdown', 'click', 'auxclick', 'contextmenu']) document.addEventListener(type, refreshReturn, true);
      window.addEventListener('pageshow', updateReturn);
      window.navigation?.addEventListener('currententrychange', () => { if (context) rememberEntry(context.token); });
      if (!window.navigation) window.addEventListener('pagehide', () => {
        if (!context || productHandle(location.href, origin) !== handle) return;
        try { mergeHistory(context.token, location.href); } catch {}
      });
    }

    const activations = new WeakMap();
    const capture = event => {
      if (event.type === 'keydown' && event.key !== 'Enter') return;
      const anchor = productAnchor(event.target);
      if (!anchor) return;
      let record;
      if (page === 'collection') record = newCollectionRecord(anchor);
      else if (context && fresh(context)) record = { ...context, token: newToken(), createdAt: Date.now(), target: productHandle(anchor.href, origin) };
      const previous = activations.get(anchor);
      if (record && event.type !== 'pointerdown' && previous && record.createdAt - previous.createdAt < 5000 && record.target === previous.target) record.token = previous.token;
      if (!record || !saveRecord(record)) return;
      activations.set(anchor, record);
      const href = addMarker(anchor.href, 'lic_return', record.token, origin);
      if (href) anchor.href = href;
    };
    for (const type of ['pointerdown', 'click', 'auxclick', 'contextmenu', 'keydown']) document.addEventListener(type, capture, true);

    if (page === 'collection') {
      const initial = collectionUrl(entryHref, origin);
      const token = initial?.searchParams.get('lic_resume');
      const record = token && records()[token];
      if (fresh(record) && record.token === token && sameView(record.url, initial.href, origin)) {
        const previous = history.state && typeof history.state === 'object' ? history.state : {};
        history.replaceState(previous, '', record.url);
        restorePosition(record);
      } else if (initial?.searchParams.has('lic_resume')) {
        initial.searchParams.delete('lic_resume');
        history.replaceState(history.state, '', initial.href);
      }
    }
  }
  function restorePosition(record) {
    let frame, card, stable = 0, stopped = false;
    const started = performance.now();
    const controller = new AbortController();
    const html = document.documentElement;
    const oldBehavior = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';
    function stop() {
      if (stopped) return;
      stopped = true;
      cancelAnimationFrame(frame);
      controller.abort();
      html.style.scrollBehavior = oldBehavior;
    }
    for (const type of ['wheel', 'touchstart', 'pointerdown', 'keydown']) window.addEventListener(type, stop, { passive: true, capture: true, signal: controller.signal });
    function attempt() {
      if (stopped) return;
      const grid = document.querySelector('#ProductGridContainer');
      if (!card?.isConnected && grid && record.anchor?.handle) {
        const link = [...grid.querySelectorAll('a[href]')].find(anchor => productHandle(anchor.href, origin) === record.anchor.handle);
        if (link) card = cardFor(link);
      }
      let target = record.scrollY;
      if (card && Number.isFinite(record.anchor.offset)) target = scrollY + card.getBoundingClientRect().top - headerBottom() - record.anchor.offset;
      target = Math.max(0, Math.min(target, document.documentElement.scrollHeight - innerHeight));
      if (Math.abs(scrollY - target) > 1) { window.scrollTo(0, target); stable = 0; } else stable++;
      const elapsed = performance.now() - started;
      if (elapsed >= 2500 || elapsed >= 500 && stable >= 8 && document.readyState === 'complete') stop();
      else frame = requestAnimationFrame(attempt);
    }
    frame = requestAnimationFrame(attempt);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
